import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import {
  Bold, Italic, Heading2, Heading3, List, ListOrdered,
  Link as LinkIcon, Image as ImageIcon, ArrowLeft, Loader2,
} from "lucide-react";
import { ImageField, normalizeImageUrl } from "./ImageField";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("nyla_admin_token")}` });

const inputCls =
  "w-full bg-[#F5F5F7] border border-[#D2D2D7] rounded-2xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#2C3E35]";

const ToolBtn = ({ onClick, active, children, label }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    className={`p-2 rounded-lg transition-colors duration-150 ${active ? "bg-[#1D1D1F] text-white" : "text-[#1D1D1F]/70 hover:bg-black/5"}`}
  >
    {children}
  </button>
);

export const PostEditor = ({ post, onDone }) => {
  const [form, setForm] = useState({
    title: post?.title || "",
    excerpt: post?.excerpt || "",
    category: post?.category || "Journal",
    image: post?.image || "",
    read_time: post?.read_time || "4 min read",
    status: post?.status || "draft",
  });
  const [saving, setSaving] = useState(false);

  const legacyHtml = post?.content?.length ? post.content.map((p) => `<p>${p}</p>`).join("") : "";

  const editor = useEditor({
    extensions: [StarterKit.configure({ link: { openOnClick: false } }), Image],
    content: post?.content_html || legacyHtml || "<p></p>",
    editorProps: {
      attributes: {
        class: "prose-editor min-h-[280px] focus:outline-none px-5 py-4 text-[15px] leading-relaxed",
        "data-testid": "post-content-editor",
      },
    },
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const addLink = () => {
    const url = window.prompt("Link URL:");
    if (url) editor.chain().focus().setLink({ href: url }).run();
  };

  const addImage = () => {
    const choice = window.prompt("Paste an image URL (Google Drive share links work too):");
    if (choice) {
      editor.chain().focus().setImage({ src: normalizeImageUrl(choice.trim()) }).run();
    }
  };

  const save = async (statusOverride) => {
    if (!form.title.trim()) return toast.error("Title is required");
    const payload = { ...form, status: statusOverride || form.status, content_html: editor?.getHTML() || "" };
    setSaving(true);
    try {
      if (post?.id) {
        await axios.put(`${API}/admin/blog/${post.id}`, payload, { headers: auth() });
      } else {
        await axios.post(`${API}/admin/blog`, payload, { headers: auth() });
      }
      toast.success(payload.status === "published" ? "Published — live on the site" : "Saved as draft");
      onDone(true);
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="post-editor">
      <button onClick={() => onDone(false)} className="flex items-center gap-1.5 text-sm text-[#86868B] hover:text-[#1D1D1F] mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to posts
      </button>

      <div className="bg-white rounded-3xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] space-y-5">
        <input data-testid="post-title-input" className={`${inputCls} text-lg font-semibold`} placeholder="Post title *" value={form.title} onChange={set("title")} />
        <textarea data-testid="post-excerpt-input" rows={2} className={`${inputCls} resize-none`} placeholder="Short excerpt (shown on the card)" value={form.excerpt} onChange={set("excerpt")} />
        <div className="grid sm:grid-cols-2 gap-4">
          <input data-testid="post-category-input" className={inputCls} placeholder="Category" value={form.category} onChange={set("category")} />
          <input data-testid="post-readtime-input" className={inputCls} placeholder="Read time (e.g. 4 min read)" value={form.read_time} onChange={set("read_time")} />
        </div>
        <div>
          <p className="text-xs font-semibold text-[#86868B] uppercase tracking-wide mb-2">Cover Image</p>
          <div data-testid="post-image-spec" className="mb-3 rounded-2xl bg-[#F5F5F7] border border-[#E5E5EA] px-4 py-3">
            <p className="text-[10px] font-semibold tracking-[0.15em] uppercase text-[#2C3E35] mb-1.5">Recommended Upload</p>
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[12px] text-[#1D1D1F]">
              <dt className="text-[#86868B]">Size</dt><dd className="font-medium">1600 × 900 px</dd>
              <dt className="text-[#86868B]">Aspect</dt><dd className="font-medium">16 : 9</dd>
              <dt className="text-[#86868B]">Format</dt><dd className="font-medium">JPG or WebP, ≤ 400 KB</dd>
            </dl>
            <p className="mt-2 text-[11px] leading-relaxed text-[#86868B]">Used on the Journal card and as the full-width hero on the article page.</p>
          </div>
          <ImageField value={form.image} onChange={(v) => setForm({ ...form, image: v })} testId="post-image-input" aiPrompt={() => `Blog cover image for an article titled "${form.title}". ${form.excerpt || ""}`} />
          {form.image && <img src={form.image} alt="cover preview" className="mt-3 h-32 rounded-2xl object-cover" />}
        </div>

        <div>
          <p className="text-xs font-semibold text-[#86868B] uppercase tracking-wide mb-2">Article Content</p>
          <div className="border border-[#D2D2D7] rounded-2xl overflow-hidden">
            <div className="flex items-center gap-1 px-3 py-2 border-b border-[#D2D2D7]/60 bg-[#F5F5F7]">
              <ToolBtn label="Bold" active={editor?.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><Bold className="w-4 h-4" /></ToolBtn>
              <ToolBtn label="Italic" active={editor?.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic className="w-4 h-4" /></ToolBtn>
              <ToolBtn label="Heading 2" active={editor?.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 className="w-4 h-4" /></ToolBtn>
              <ToolBtn label="Heading 3" active={editor?.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><Heading3 className="w-4 h-4" /></ToolBtn>
              <ToolBtn label="Bullet list" active={editor?.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}><List className="w-4 h-4" /></ToolBtn>
              <ToolBtn label="Numbered list" active={editor?.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered className="w-4 h-4" /></ToolBtn>
              <ToolBtn label="Add link" active={editor?.isActive("link")} onClick={addLink}><LinkIcon className="w-4 h-4" /></ToolBtn>
              <ToolBtn label="Insert image" onClick={addImage}><ImageIcon className="w-4 h-4" /></ToolBtn>
            </div>
            <EditorContent editor={editor} />
          </div>
        </div>

        <div className="flex items-center gap-3 pt-2">
          <button
            data-testid="post-save-draft"
            onClick={() => save("draft")}
            disabled={saving}
            className="px-6 py-3 rounded-full border border-[#D2D2D7] text-sm font-medium hover:bg-black/5 disabled:opacity-60"
          >
            Save Draft
          </button>
          <button
            data-testid="post-publish"
            onClick={() => save("published")}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-3 rounded-full bg-[#2C3E35] text-white text-sm font-medium hover:bg-[#223129] disabled:opacity-60"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Publish
          </button>
        </div>
      </div>
    </div>
  );
};
